import React, { useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { Campo } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import { useToken } from 'Hook/Authentication';
import Certificado from 'Interface/Certificados';
import { useEditarCertificadoMutation, useRebuildCertificateMutation } from 'Service';

interface Props {
  dados: Certificado;
}

const regras = object({
  codigo_de_rastreio: string().nullable().optional(),
});

const Lista = ({ dados }: Props): React.ReactElement => {
  const token = useToken();
  const toast = useToast();
  const [modal, setModal] = useState<boolean>(false);
  const [editarCertificado] = useEditarCertificadoMutation();
  const [rebuildCertificate] = useRebuildCertificateMutation();

  const marcarComoEmitido = async (): Promise<void> => {
    try {
      await editarCertificado({
        id: dados.id,
        emitido: dados.emitido ? 0 : 1,
      }).unwrap();
    } catch (erro) {
      toast({
        tema: 'danger',
        titulo: 'Erro ao atualizar',
        conteudo: 'Verifique os logs para mais informações',
      });
    }
  };

  const marcarComoEnviado = async (certificado: Certificado): Promise<void> => {
    try {
      await editarCertificado({
        id: dados.id,
        emitido: 1,
        enviado: 1,
        codigo_de_rastreio: certificado.codigo_de_rastreio,
      }).unwrap();
    } catch (erro) {
      toast({
        tema: 'danger',
        titulo: 'Erro ao atualizar',
        conteudo: 'Verifique os logs para mais informações',
      });
    }
  };

  const desmarcarComoEnviado = async (): Promise<void> => {
    try {
      await editarCertificado({
        id: dados.id,
        enviado: 0,
        codigo_de_rastreio: '',
      }).unwrap();
    } catch (erro) {
      toast({
        tema: 'danger',
        titulo: 'Erro ao atualizar',
        conteudo: 'Verifique os logs para mais informações',
      });
    }
  };

  const invalidar = async (): Promise<void> => {
    try {
      await editarCertificado({
        id: dados.id,
        invalidado: 1,
      }).unwrap();
    } catch (erro) {
      toast({
        tema: 'danger',
        titulo: 'Erro ao invalidar',
        conteudo: 'Verifique os logs para mais informações',
      });
    }
  };

  const rebuild = async (): Promise<void> => {
    try {
      await rebuildCertificate(dados).unwrap();
    } catch (erro) {
      toast({
        tema: 'danger',
        titulo: 'Erro ao gerar',
        conteudo: 'Verifique os logs para mais informações',
      });
    }
  };

  return (
    <div className="d-flex border-bottom align-items-center py-2 px-3 gap-3">
      <a
        href={`/certificados/imprimir/${dados.id}?token=${token}`}
        className="position-relative me-3"
        target="_blank"
        rel="noreferrer"
      >
        <Imagem
          src={dados.curso?.imagem}
          alt={dados.curso?.nome}
          className={[
            'rounded-circle',
            'object-fit-cover',
            'border border-white border-3',
          ].class()}
          width="80"
          height="80"
        />
        <Imagem
          src={dados.usuario?.imagem}
          alt={dados.usuario?.nome}
          className={[
            'rounded-circle',
            'object-fit-cover',
            'border border-white border-3',
            'position-absolute top-50 start-100',
            'translate-middle',
          ].class()}
          width="80"
          height="80"
        />
      </a>
      <div className="ps-4 fs-5 text-truncate">
        <a
          href={`/certificados/imprimir/${dados.id}?token=${token}`}
          className="text-decoration-none"
          target="_blank"
          rel="noreferrer"
        >
          {dados.usuario?.nome}
        </a>
        <small className="d-block fs-6 text-muted text-truncate">
          <Icone nome="pen-fill" className="me-2" />
          {dados.curso?.nome}
        </small>
      </div>
      <BrowserView className="d-flex flex-grow-1 align-items-center justify-content-end gap-5">
        {dados.formato == 2 && (
          <React.Fragment>
            <div className="fs-5 text-end">
              {dados.invalidado == 1 && (
                <span className="badge text-bg-danger">Invalidado</span>
              )}
              <Botao
                lg
                tema="transparent"
                onClick={marcarComoEmitido}
                confirmacao="Tem certeza que deseja marcar esse certificado como emitido/não emitido?"
                className="border-0"
              >
                {dados.emitido ? (
                  <Icone nome="check2-circle" className="text-success" />
                ) : (
                  <Icone nome="x-circle" className="text-danger" />
                )}
              </Botao>
              {!dados.enviado ? (
                <Botao
                  lg
                  tema="transparent"
                  onClick={() => setModal(true)}
                  className="border-0"
                >
                  <Icone nome="send-x" className="text-danger" />
                </Botao>
              ) : (
                <Botao
                  lg
                  tema="transparent"
                  onClick={desmarcarComoEnviado}
                  confirmacao="Tem certeza que deseja desmarcar esse certificado como enviado?"
                  className="border-0"
                >
                  <Icone nome="send-check" className="text-success" />
                </Botao>
              )}
            </div>
            {dados.codigo_de_rastreio && (
              <div className="fs-6 text-end text-nowrap">
                {dados.codigo_de_rastreio}
                <small className="d-block fs-6 text-muted">
                  <Icone nome="upc-scan" className="me-2" />
                  Cod. de Rastreio
                </small>
              </div>
            )}
            <div className="fs-6 text-end text-nowrap">
              {dados.frete}
              <small className="d-block fs-6 text-muted">
                <Icone nome="truck" className="me-2" />
                Frete
              </small>
            </div>
          </React.Fragment>
        )}
        <div className="fs-6 text-end text-nowrap">
          {dados.criado.data().toLocaleString()}
          <small className="d-block fs-6 text-muted">
            <Icone nome="calendar" className="me-2" />
            Criado em
          </small>
        </div>
        <div className="fs-5 text-end d-flex gap-2 align-items-center">
          <BotaoLink
            icone="cloud-download"
            tema="success"
            to={dados.url}
            target="_blank"
          />
          {!dados.invalidado && (
            <Botao
              icone="file-earmark-x"
              tema="danger"
              onClick={invalidar}
              confirmacao="Tem certiza que deseja invalidar esse certificado? Você vai liberar o aluno para uma nova solicitação."
            />
          )}
          <Botao
            icone="arrow-clockwise"
            onClick={rebuild}
            confirmacao="Tem certiza que deseja gerar novamente esse certificado?"
          />
        </div>
      </BrowserView>
      <Modal
        exibir={modal}
        aoFechar={setModal}
        titulo="Marcar certificado como enviado"
      >
        <Form onSubmit={marcarComoEnviado} regras={regras}>
          <Campo
            name="codigo_de_rastreio"
            rotulo="Código de rastreio"
            ajuda="Se não houver um código de rastreio, não é necessário coloca-lo"
          />
          <Botao type="submit">Marcar como enviado</Botao>
        </Form>
      </Modal>
    </div>
  );
};

export default Lista;
